import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { menuSections } from "../../data/mockData";
import { logoutAgentAndRedirect } from "../../lib/agentAuth";
import { BRAND } from "../../lib/brand";
import { filterMenuSections } from "../../lib/agencyAccess";

function isActiveLink(pathname, path) {
  return pathname === path || pathname.startsWith(`${path}/`);
}

function MenuIcon({ icon }) {
  return (
    <span className="menu-icon" aria-hidden="true">
      <iconify-icon icon={icon} />
    </span>
  );
}

function MenuItem({ to, icon, label, active, title }) {
  return (
    <Link to={to} className={`menu-item${active ? " active-page" : ""}`}>
      <MenuIcon icon={icon} />
      <span className="menu-label">{label}</span>
      {title ? (
        <span className="sidebar-tooltip" role="tooltip" aria-hidden="true">
          {title}
        </span>
      ) : null}
    </Link>
  );
}

export default function Sidebar({
  collapsed,
  mobileOpen,
  onCloseMobile,
  agencyRole = "",
}) {
  const location = useLocation();
  const pathname = location.pathname.replace(/\/+$/, "") || "/dashboard";
  const [openDropdowns, setOpenDropdowns] = useState({});
  const visibleSections = filterMenuSections(menuSections, agencyRole);

  function isDropdownExpanded(section) {
    const forceOpen = (section.items || []).some((item) => isActiveLink(pathname, item.path));
    return forceOpen || Boolean(openDropdowns[section.title]);
  }

  async function handleLogout() {
    await logoutAgentAndRedirect();
  }

  return (
    <aside className={`sidebar ${collapsed ? "active" : ""} ${mobileOpen ? "sidebar-open" : ""}`}>
      <button type="button" className="sidebar-close-btn" onClick={onCloseMobile} aria-label="Close sidebar">
        <iconify-icon icon="radix-icons:cross-2"></iconify-icon>
      </button>
      <div>
        <Link to="/dashboard" className="sidebar-logo">
          <img src={BRAND.logo} alt={BRAND.name} className="light-logo" />
          <img src={BRAND.logoDark} alt={BRAND.name} className="dark-logo" />
          <img src={BRAND.logoIcon} alt={BRAND.name} className="logo-icon" />
        </Link>
      </div>
      <div className="sidebar-menu-area">
        <ul className="sidebar-menu" id="sidebar-menu">
          {visibleSections.map((section, index) => {
            if (section.type === "dropdown") {
              const isExpanded = isDropdownExpanded(section);
              return (
                <li key={section.title} className={`dropdown ${isExpanded ? "dropdown-open open" : ""}`}>
                  <a
                    href={`#${String(section.title || "menu").toLowerCase().replace(/\s+/g, "-")}`}
                    className="menu-item"
                    title={collapsed ? section.title : undefined}
                    aria-expanded={isExpanded}
                    onClick={(event) => {
                      event.preventDefault();
                      setOpenDropdowns((current) => ({
                        ...current,
                        [section.title]: !current[section.title],
                      }));
                    }}
                  >
                    <MenuIcon icon={section.icon} />
                    <span className="menu-label">{section.title}</span>
                    {collapsed ? (
                      <span className="sidebar-tooltip" role="tooltip" aria-hidden="true">
                        {section.title}
                      </span>
                    ) : null}
                  </a>
                  {!collapsed ? (
                    <ul className={`sidebar-submenu ${isExpanded ? "show" : ""}`}>
                      {section.items.map((item) => (
                        <li key={item.path} className={isActiveLink(pathname, item.path) ? "active-page" : ""}>
                          <MenuItem
                            to={item.path}
                            icon={item.icon}
                            label={item.label}
                            active={isActiveLink(pathname, item.path)}
                            title={collapsed ? item.label : undefined}
                          />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            }

            return (
              <React.Fragment key={section.title || `section-${index}`}>
                {section.title ? <li className="sidebar-menu-group-title">{section.title}</li> : null}
                {section.items.map((item) => (
                  <li key={item.path}>
                    <MenuItem
                      to={item.path}
                      icon={item.icon}
                      label={item.label}
                      active={isActiveLink(pathname, item.path)}
                      title={collapsed ? item.label : undefined}
                    />
                  </li>
                ))}
              </React.Fragment>
            );
          })}
        </ul>
      </div>
      <div className="sidebar-footer">
        <button
          type="button"
          className="menu-item sidebar-logout"
          onClick={handleLogout}
        >
          <MenuIcon icon="lucide:power" />
          <span className="menu-label">Logout</span>
          {collapsed ? (
            <span className="sidebar-tooltip" role="tooltip" aria-hidden="true">
              Logout
            </span>
          ) : null}
        </button>
      </div>
    </aside>
  );
}
